/**
 * SafeIntersections layer config — the layer-style JSON imports, the
 * per-module builder calls (`buildSystemLayers`), and the module grouping
 * axis (`modules`).
 */

import type { CircleLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import type { ModuleDefinition } from '#lib/apps/types.js';
import IconModuleIdentify from '#lib/assets/modules/identify.svg?raw';
import IconModulePrioritize from '#lib/assets/modules/prioritize.svg?raw';
import IconModuleInterventions from '#lib/assets/modules/interventions.svg?raw';
import {
	EIdentify,
	EPrioritize,
	EProduct,
	ESafeIntersectionsModule,
	type ISafeIntersectionsFeature,
	type ISafeIntersectionsSystemLayers
} from './interfaces';
import { getVectorLayerSource, groupByModule } from '#lib/common/layers.js';

// --- Layer style JSON imports ---
import styleIdentify from './layer_styles/safeintersections_identify_style.json';
import stylePrioritize from './layer_styles/safeintersections_prioritize_style.json';

// GeoServer dataset this app owns; drives availability and layer filtering.
export const SI_DATASET = 'safeintersections';

/**
 * UI-only module id for the Interventions legend tab. Interventions has no
 * map layers (the calculator on Identify features is the interventions tool),
 * so it is deliberately NOT part of `ESafeIntersectionsModule` — that enum
 * drives the system-data envelope and legend parsing.
 */
export const INTERVENTIONS_MODULE_ID = 'Interventions';

/**
 * Module grouping axis — drives the legend tabs. Identify and Prioritize
 * carry operational layers; Interventions is a static legend tab only.
 */
export const modules: ModuleDefinition[] = [
	{ id: ESafeIntersectionsModule.Identify, label: 'Identify', icon: IconModuleIdentify },
	{ id: ESafeIntersectionsModule.Prioritize, label: 'Prioritize', icon: IconModulePrioritize },
	{ id: INTERVENTIONS_MODULE_ID, label: 'Interventions', icon: IconModuleInterventions }
];

/**
 * Build the nested `ISystemLayers` from a flat list of operational features.
 *
 * Returns null when SafeIntersections product data is missing — caller treats
 * this as a system-data load failure.
 */
export function buildSystemLayers(
	appLayers: ISafeIntersectionsFeature[]
): ISafeIntersectionsSystemLayers | null {
	const siLayers = groupByModule(appLayers, EProduct.SafeIntersections, SI_DATASET);
	if (!siLayers) return null;

	return {
		[ESafeIntersectionsModule.Identify]: {
			[EIdentify.Identify]: getVectorLayerSource(
				EIdentify.Identify,
				siLayers[ESafeIntersectionsModule.Identify]?.[0],
				{
					type: 'circle',
					layout: styleIdentify.layout as CircleLayerSpecification['layout'],
					paint: styleIdentify.paint as CircleLayerSpecification['paint'],
					minzoom: 6
				},
				3
			)
		},
		[ESafeIntersectionsModule.Prioritize]: {
			[EPrioritize.Prioritize]: getVectorLayerSource(
				EPrioritize.Prioritize,
				siLayers[ESafeIntersectionsModule.Prioritize]?.[0],
				{
					type: 'symbol',
					layout: stylePrioritize.layout as SymbolLayerSpecification['layout'],
					paint: stylePrioritize.paint as SymbolLayerSpecification['paint'],
					minzoom: 1
				},
				0
			)
		}
	};
}
